import { useState, useEffect } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from '@/shared/hooks'
import { Loader2, AlertCircle, ArrowLeft, Send } from 'lucide-react'
import { toast } from 'react-toastify'
import { Sidebar } from '@/shared/components'
import { cn } from '@/shared/lib/utils'
import { useProjectByShortId, useUpdateProject, useTeam } from '../hooks'

const EXISTING_ASSETS_OPTIONS = [
  'starting_from_scratch',
  'existing_codebase',
  'existing_designs',
  'other',
] as const

type ExistingAssetsOption = (typeof EXISTING_ASSETS_OPTIONS)[number]

interface FormFieldProps {
  label: string
  required?: boolean
  hint?: string
  error?: string
  touched?: boolean
  children: React.ReactNode
}

function FormField({ label, required, hint, error, touched, children }: FormFieldProps) {
  const showError = touched && error

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1">
        <label className="text-sm font-medium text-foreground">{label}</label>
        {required ? (
          <span className="text-sm font-medium text-accent">*</span>
        ) : (
          <span className="text-xs text-muted-foreground">(optional)</span>
        )}
      </div>
      {children}
      {showError ? (
        <p className="flex items-center gap-1 text-xs text-destructive">
          <AlertCircle className="h-3 w-3" />
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  )
}

export function ProjectEditPage() {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { shortId } = useParams<{ shortId: string }>()

  const { data: project, isLoading, error: loadError } = useProjectByShortId(shortId)
  const { data: team } = useTeam(project?.team_id)
  const updateProject = useUpdateProject(project?.id || '')

  const [formData, setFormData] = useState({
    projectName: '',
    description: '',
    objective: '',
    targetUsers: '',
    existingAssets: 'starting_from_scratch' as ExistingAssetsOption,
    constraints: '',
    inspirations: '',
  })

  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [submitAttempted, setSubmitAttempted] = useState(false)

  // Pre-fill form when project data is loaded
  useEffect(() => {
    if (project) {
      setFormData({
        projectName: project.name || '',
        description: project.description || '',
        objective: project.objective || '',
        targetUsers: project.target_users || '',
        existingAssets: (project.existing_assets as ExistingAssetsOption) || 'starting_from_scratch',
        constraints: project.constraints || '',
        inspirations: project.inspirations || '',
      })
    }
  }, [project])

  // Redirect if project is not pending or not rejected
  useEffect(() => {
    if (project && project.status !== 'pending') {
      navigate(`/p/${shortId}`, { replace: true })
    }
  }, [project, shortId, navigate])

  const handleChange = (
    field: keyof typeof formData,
    value: string | ExistingAssetsOption
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }))
  }

  const getFieldError = (field: keyof typeof formData): string | undefined => {
    const value = formData[field]
    if (field === 'projectName' && (!value || (typeof value === 'string' && !value.trim()))) {
      return t('precadrage.form.errors.required')
    }
    if (field === 'description' && (!value || (typeof value === 'string' && !value.trim()))) {
      return t('precadrage.form.errors.required')
    }
    if (field === 'objective' && (!value || (typeof value === 'string' && !value.trim()))) {
      return t('precadrage.form.errors.required')
    }
    if (field === 'targetUsers' && (!value || (typeof value === 'string' && !value.trim()))) {
      return t('precadrage.form.errors.required')
    }
    return undefined
  }

  const isFieldTouched = (field: string) => touched[field] || submitAttempted

  const isFormValid =
    formData.projectName.trim() !== '' &&
    formData.description.trim() !== '' &&
    formData.objective.trim() !== '' &&
    formData.targetUsers.trim() !== ''

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitAttempted(true)

    if (!isFormValid || !project) {
      return
    }

    try {
      await updateProject.mutateAsync({
        name: formData.projectName.trim(),
        description: formData.description.trim(),
        objective: formData.objective.trim(),
        target_users: formData.targetUsers.trim(),
        existing_assets: formData.existingAssets,
        constraints: formData.constraints.trim() || undefined,
        inspirations: formData.inspirations.trim() || undefined,
      })

      toast.success(t('projectEdit.resubmitSuccess'))
      navigate(`/p/${shortId}`)
    } catch {
      toast.error(t('common.error'))
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar />
        <main className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-foreground-muted" />
        </main>
      </div>
    )
  }

  if (loadError || !project) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar />
        <main className="flex flex-1 flex-col items-center justify-center gap-2 text-destructive">
          <AlertCircle className="h-8 w-8" />
          <p>{t('common.error')}</p>
        </main>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />

      <main className="flex flex-1 flex-col overflow-auto bg-background-secondary">
        {/* Header */}
        <div className="flex items-center gap-4 px-8 py-6">
          {/* Back button */}
          <button
            onClick={() => navigate(`/p/${shortId}`)}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-white text-foreground transition-colors hover:bg-background-secondary"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>

          {/* Title group */}
          <div className="flex flex-col gap-1">
            <span className="text-xs text-foreground-muted">
              {team?.name || ''} / {project?.name || ''}
            </span>
            <h1 className="text-xl font-semibold text-foreground">
              {t('projectEdit.title')}
            </h1>
            <p className="text-xs text-foreground-muted">
              {t('projectEdit.subtitle')}
            </p>
          </div>
        </div>

        {/* Form Content */}
        <div className="flex-1 p-8">
          <form onSubmit={handleSubmit} className="mx-auto max-w-2xl space-y-6">
            {/* Global Error */}
            {updateProject.error && (
              <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {updateProject.error.message}
              </div>
            )}

            {/* Project Name */}
            <FormField
              label={t('precadrage.form.projectName')}
              required
              error={getFieldError('projectName')}
              touched={isFieldTouched('projectName')}
            >
              <input
                type="text"
                value={formData.projectName}
                onChange={(e) => handleChange('projectName', e.target.value)}
                onBlur={() => handleBlur('projectName')}
                placeholder={t('precadrage.form.projectNamePlaceholder')}
                className={cn(
                  'w-full rounded-lg border bg-background px-3.5 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1',
                  isFieldTouched('projectName') && getFieldError('projectName')
                    ? 'border-destructive focus:border-destructive focus:ring-destructive'
                    : 'border-border focus:border-accent focus:ring-accent'
                )}
              />
            </FormField>

            {/* Description */}
            <FormField
              label={t('precadrage.form.description')}
              required
              hint={!getFieldError('description') ? t('precadrage.form.descriptionHint') : undefined}
              error={getFieldError('description')}
              touched={isFieldTouched('description')}
            >
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                onBlur={() => handleBlur('description')}
                placeholder={t('precadrage.form.descriptionPlaceholder')}
                rows={4}
                className={cn(
                  'w-full resize-none rounded-lg border bg-background px-3.5 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1',
                  isFieldTouched('description') && getFieldError('description')
                    ? 'border-destructive focus:border-destructive focus:ring-destructive'
                    : 'border-border focus:border-accent focus:ring-accent'
                )}
              />
            </FormField>

            {/* Objective */}
            <FormField
              label={t('precadrage.form.objective')}
              required
              error={getFieldError('objective')}
              touched={isFieldTouched('objective')}
            >
              <textarea
                value={formData.objective}
                onChange={(e) => handleChange('objective', e.target.value)}
                onBlur={() => handleBlur('objective')}
                placeholder={t('precadrage.form.objectivePlaceholder')}
                rows={3}
                className={cn(
                  'w-full resize-none rounded-lg border bg-background px-3.5 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1',
                  isFieldTouched('objective') && getFieldError('objective')
                    ? 'border-destructive focus:border-destructive focus:ring-destructive'
                    : 'border-border focus:border-accent focus:ring-accent'
                )}
              />
            </FormField>

            {/* Target Users */}
            <FormField
              label={t('precadrage.form.targetUsers')}
              required
              error={getFieldError('targetUsers')}
              touched={isFieldTouched('targetUsers')}
            >
              <input
                type="text"
                value={formData.targetUsers}
                onChange={(e) => handleChange('targetUsers', e.target.value)}
                onBlur={() => handleBlur('targetUsers')}
                placeholder={t('precadrage.form.targetUsersPlaceholder')}
                className={cn(
                  'w-full rounded-lg border bg-background px-3.5 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1',
                  isFieldTouched('targetUsers') && getFieldError('targetUsers')
                    ? 'border-destructive focus:border-destructive focus:ring-destructive'
                    : 'border-border focus:border-accent focus:ring-accent'
                )}
              />
            </FormField>

            {/* Existing Assets */}
            <FormField
              label={t('precadrage.form.existingAssets')}
              required
              hint={t('precadrage.form.existingAssetsHint')}
            >
              <select
                value={formData.existingAssets}
                onChange={(e) =>
                  handleChange('existingAssets', e.target.value as ExistingAssetsOption)
                }
                className="w-full rounded-lg border border-border bg-background px-3.5 py-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              >
                {EXISTING_ASSETS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {t(`precadrage.form.existingAssetsOptions.${option}`)}
                  </option>
                ))}
              </select>
            </FormField>

            {/* Constraints */}
            <FormField label={t('precadrage.form.constraints')}>
              <textarea
                value={formData.constraints}
                onChange={(e) => handleChange('constraints', e.target.value)}
                placeholder={t('precadrage.form.constraintsPlaceholder')}
                rows={3}
                className="w-full resize-none rounded-lg border border-border bg-background px-3.5 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </FormField>

            {/* Inspirations */}
            <FormField label={t('precadrage.form.inspirations')}>
              <textarea
                value={formData.inspirations}
                onChange={(e) => handleChange('inspirations', e.target.value)}
                placeholder={t('precadrage.form.inspirationsPlaceholder')}
                rows={2}
                className="w-full resize-none rounded-lg border border-border bg-background px-3.5 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </FormField>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={!isFormValid || updateProject.isPending}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-accent px-6 py-3 font-semibold text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Send className="h-[18px] w-[18px]" />
                {updateProject.isPending
                  ? t('projectEdit.resubmitting')
                  : t('projectEdit.resubmit')}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  )
}
